// src/pages/HistorialVentas.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/HistorialVentas.css';

const TZ = 'America/Bogota';

const dinero = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const numero = (n) => Number(n || 0).toLocaleString('es-CO');

// Día civil de Bogotá de un instante, como 'YYYY-MM-DD'. Es la misma regla
// que usan los reportes, así que los rangos coinciden con lo que ya validamos.
const fmtDia = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
});
const diaBogota = (iso) => fmtDia.format(new Date(iso));

const desplazar = (iso, dias) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + dias));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

const hoyBogota = () => fmtDia.format(new Date());

const primeroDelMes = () => {
  const [y, m] = hoyBogota().split('-');
  return `${y}-${m}-01`;
};

const ATAJOS = [
  { id: 'hoy', texto: 'Hoy', rango: () => [hoyBogota(), hoyBogota()] },
  { id: 'ayer', texto: 'Ayer', rango: () => [desplazar(hoyBogota(), -1), desplazar(hoyBogota(), -1)] },
  { id: '7', texto: 'Últimos 7 días', rango: () => [desplazar(hoyBogota(), -6), hoyBogota()] },
  { id: 'mes', texto: 'Este mes', rango: () => [primeroDelMes(), hoyBogota()] },
];

const HistorialVentas = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [ventas, setVentas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [desde, setDesde] = useState(() => desplazar(hoyBogota(), -6));
  const [hasta, setHasta] = useState(() => hoyBogota());
  const [metodo, setMetodo] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [texto, setTexto] = useState('');
  const [expandida, setExpandida] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [rVentas, rEmpleados] = await Promise.all([
        axios.get(`${apiUrl}/api/ventas`),
        axios.get(`${apiUrl}/api/empleados/empleados`),
      ]);
      setVentas(Array.isArray(rVentas.data) ? rVentas.data : []);
      setEmpleados(Array.isArray(rEmpleados.data) ? rEmpleados.data : []);
    } catch {
      setError('No se pudo cargar el historial de ventas.');
    } finally {
      setCargando(false);
    }
  }, [apiUrl]);

  useEffect(() => { cargar(); }, [cargar]);

  const metodos = useMemo(
    () => Array.from(new Set(ventas.map((v) => v.metodo_pago).filter(Boolean))).sort(),
    [ventas]
  );

  const filtradas = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return ventas.filter((v) => {
      const dia = diaBogota(v.fecha);
      if (desde && dia < desde) return false;
      if (hasta && dia > hasta) return false;
      if (metodo && v.metodo_pago !== metodo) return false;
      if (vendedor && String(v.vendedor_id) !== String(vendedor)) return false;
      if (!q) return true;
      return (
        String(v.ventas_id).includes(q) ||
        (v.cliente || '').toLowerCase().includes(q) ||
        (v.metodo_pago || '').toLowerCase().includes(q)
      );
    });
  }, [ventas, desde, hasta, metodo, vendedor, texto]);

  const resumen = useMemo(() => {
    const monto = filtradas.reduce((s, v) => s + Number(v.total || 0), 0);
    return { monto, cuenta: filtradas.length, promedio: filtradas.length ? Math.round(monto / filtradas.length) : 0 };
  }, [filtradas]);

  const atajoActivo = ATAJOS.find((a) => {
    const [d, h] = a.rango();
    return d === desde && h === hasta;
  });

  const aplicarAtajo = (a) => { const [d, h] = a.rango(); setDesde(d); setHasta(h); };

  const limpiar = () => { setMetodo(''); setVendedor(''); setTexto(''); };
  const hayFiltros = metodo || vendedor || texto;

  const nombreVendedor = (id) => {
    const e = empleados.find((x) => String(x.empleado_id) === String(id));
    return e ? `${e.nombre} ${e.apellido}` : '—';
  };

  const renglonesDe = (v) => v.VentaDetalles || [];

  return (
    <div className="historial">
      <div className="encabezado-pagina">
        <div>
          <h1>Historial de ventas</h1>
          <p className="subtitulo">Consulta, filtra y revisa el detalle de cada factura.</p>
        </div>
        <button className="secundario" onClick={cargar} disabled={cargando}>
          {cargando ? 'Actualizando…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="aviso-bloque error" role="alert">{error}</p>}

      <section className="tarjeta-base">
        <div className="atajos-rango">
          {ATAJOS.map((a) => (
            <button key={a.id} className={`chip-rango ${atajoActivo?.id === a.id ? 'activo' : ''}`}
              aria-pressed={atajoActivo?.id === a.id} onClick={() => aplicarAtajo(a)}>
              {a.texto}
            </button>
          ))}
        </div>

        <div className="filtros-historial">
          <div className="campo"><label htmlFor="f-desde">Desde</label>
            <input id="f-desde" type="date" value={desde} max={hasta}
              onChange={(e) => setDesde(e.target.value)} /></div>
          <div className="campo"><label htmlFor="f-hasta">Hasta</label>
            <input id="f-hasta" type="date" value={hasta} min={desde}
              onChange={(e) => setHasta(e.target.value)} /></div>
          <div className="campo"><label htmlFor="f-metodo">Método</label>
            <select id="f-metodo" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
              <option value="">Todos</option>
              {metodos.map((m) => <option key={m} value={m}>{m}</option>)}
            </select></div>
          <div className="campo"><label htmlFor="f-vendedor">Vendedor</label>
            <select id="f-vendedor" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              <option value="">Todos</option>
              {empleados.map((e) => (
                <option key={e.empleado_id} value={e.empleado_id}>{e.nombre} {e.apellido}</option>
              ))}
            </select></div>
          <div className="campo crece"><label htmlFor="f-texto">Buscar</label>
            <input id="f-texto" type="search" value={texto} placeholder="Número de factura o cliente…"
              onChange={(e) => setTexto(e.target.value)} /></div>
          {hayFiltros && (
            <button className="fantasma limpiar-filtros" onClick={limpiar}>Limpiar filtros</button>
          )}
        </div>

        {/* El resumen es del rango filtrado, no de todo el historial. */}
        <div className="resumen-rango">
          <div><span className="resumen-etiqueta">Total del rango</span><strong className="resumen-valor">{dinero(resumen.monto)}</strong></div>
          <div><span className="resumen-etiqueta">Facturas</span><strong className="resumen-valor">{numero(resumen.cuenta)}</strong></div>
          <div><span className="resumen-etiqueta">Ticket promedio</span><strong className="resumen-valor">{dinero(resumen.promedio)}</strong></div>
        </div>
      </section>

      <section className="tarjeta-base">
        {filtradas.length === 0 ? (
          <div className="estado-vacio">
            <h3>{cargando ? 'Cargando…' : 'Sin facturas en este rango'}</h3>
            <p>{cargando ? '' : 'Prueba con otras fechas o quita los filtros.'}</p>
          </div>
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Fecha</th><th>Cliente</th><th>Vendedor</th>
                  <th>Método</th><th className="cifra">Total</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((v) => {
                  const abierta = expandida === v.ventas_id;
                  const renglones = renglonesDe(v);
                  return (
                    <React.Fragment key={v.ventas_id}>
                      <tr className={abierta ? 'fila-abierta' : ''}>
                        <td>{v.ventas_id}</td>
                        <td>{new Date(v.fecha).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td className="celda-recortada">{v.cliente || '—'}</td>
                        <td className="celda-recortada">{nombreVendedor(v.vendedor_id)}</td>
                        <td><span className="distintivo neutro">{v.metodo_pago || '—'}</span></td>
                        <td className="cifra">{dinero(v.total)}</td>
                        <td>
                          <div className="acciones-fila">
                            <button className="secundario pequeno"
                              aria-expanded={abierta}
                              onClick={() => setExpandida(abierta ? null : v.ventas_id)}>
                              {abierta ? 'Ocultar' : `Detalle (${renglones.length})`}
                            </button>
                            <Link to={`/facturacion/imprimir/${v.ventas_id}`} className="enlace-boton">Imprimir</Link>
                          </div>
                        </td>
                      </tr>
                      {abierta && (
                        <tr className="fila-detalle">
                          <td colSpan={7}>
                            {renglones.length === 0 ? (
                              <p className="texto-auxiliar">Esta factura no tiene renglones registrados.</p>
                            ) : (
                              <>
                              <p className="detalle-titulo">Renglones de la factura</p>
                              <ul className="detalle-lista">
                                {renglones.map((d, i) => {
                                  const nombre = d.producto?.name || d.bebida?.name || d.tipo_producto;
                                  return (
                                    <li key={i}>
                                      <span className="detalle-nombre">{nombre}</span>
                                      <span className="detalle-cantidad">×{numero(d.cantidad)}</span>
                                      <span className="detalle-precio">{dinero(d.precio)} c/u</span>
                                      <span className="detalle-subtotal">{dinero(d.precio * d.cantidad)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default HistorialVentas;
